import { STATUS_LABEL, STATUS_TONE, label } from '../lib/vocabulary.ts'
import { Tooltip } from './Tooltip.tsx'

/**
 * What status a conferência is in, in one badge.
 *
 * Shared by the queue and the conferência itself, because a person moving
 * between them is following one thing and two differently-drawn labels for the
 * same fact read as two facts.
 *
 * Coloured by **whose turn it is**, not by severity: ours is brand, a
 * pharmacist's is red, comercial's is amber, finished is green. Somebody
 * scanning a queue is asking "is this mine", and a palette that ranks statuses
 * by how bad they are answers a question nobody asked.
 */
export function StatusBadge({
  status,
  reason,
  className = '',
}: {
  status: string
  /**
   * Why it stopped, shown on the badge itself.
   *
   * A conferência that ends badly ends in one word -- "Cancelada" -- and the
   * sentence saying which of several things happened lived only on the
   * conferência's own screen, if anywhere. "O orçamento foi excluído no ERP"
   * and "a análise parou" ask for opposite things from whoever reads them,
   * and the badge was showing neither.
   */
  reason?: string | null | undefined
  className?: string
}) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE['waiting']!
  const badge = (
    /* No dot: the badge is already the colour the dot was repeating. */
    <span
      className={`text-tSmall inline-flex min-w-0 items-center rounded-full px-2 py-[2px] ${className}`}
      style={{ backgroundColor: tone.fill, color: tone.text }}
    >
      <span className="truncate">{label(STATUS_LABEL, status)}</span>
    </span>
  )

  // Wrapped only where there is something to say: a tooltip that opens on
  // every badge and is empty on most of them teaches people to ignore it.
  return reason === undefined || reason === null || reason === '' ? (
    badge
  ) : (
    <Tooltip text={reason}>{badge}</Tooltip>
  )
}
